import * as Sentry from '@sentry/react-native'
import { version, build } from '@/package.json'
import * as Updates from 'expo-updates'
import { environment, isE2E, isProduction } from '@/src/common/environment'
import { useNavigationContainerRef } from 'expo-router'
import { useEffect, type PropsWithChildren } from 'react'
import { useUserStore } from '@/src/common/store/useUserStore'
import { stringifyMessage } from '@/src/common/hooks/useLogError/stringifyMessage'
import { isNetworkRequestFailedDueToUnreachableInternet } from '@/src/common/utils/isNetworkRequestFailedDueToUnreachableInternet'

const routingInstrumentationIntegration = Sentry.reactNavigationIntegration({
  enableTimeToInitialDisplay: !__DEV__,
})

// copy of server side redacting:
// https://github.com/Acre-Rewards/acre-rewards-api/blob/main/src/common/logger/redacting-logger.ts#L5
const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g
/** This regex attempts to match various phone number formats */
const phoneRegex = /(\+\d{1,3}[\s.-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g
const ssnRegex = /\b\d{3}-\d{2}-\d{4}\b/g

/**
 * Creates a message redacter that, in production, replaces sensitive data.
 */
export function createMessageRedacter() {
  return isProduction
    ? function redactSensitiveInfo(message: unknown) {
        return stringifyMessage(message)
          .replace(emailRegex, '[REDACTED EMAIL]')
          .replace(phoneRegex, '[REDACTED PHONE NUMBER]')
          .replace(ssnRegex, '[REDACTED SSN]')
      }
    : stringifyMessage
}
export const redactMessage = createMessageRedacter()

Sentry.init({
  dsn:
    environment == 'local'
      ? ''
      : 'https://2974d499f2bee18ed51071de74eadfd7:ec5041c6570eab2269826dd1dfc4d592@o4508015997747200.ingest.us.sentry.io/4508094837555200',
  environment,
  release: version,
  dist: build,
  patchGlobalPromise: false,
  _experiments: {
    enableUnhandledCPPExceptionsV2: true,
  },
  ignoreErrors: [
    'Attempted to navigate before mounting the Root Layout component.',
    'error opening points transfer portal',
    /^Unauthorized$/,
    'geo lookup gave an empty state code',
    /^State code .* not found in state mapping parameter/,
  ],
  ...(isE2E
    ? undefined
    : {
        enableNativeFramesTracking: !__DEV__,
        enableAppStartTracking: !__DEV__,
        tracesSampleRate: isProduction ? 0.5 : 1.0,
        // Profile 100% of traces
        profilesSampleRate: 1.0,
        attachStacktrace: true,
        beforeSend(event) {
          // Tag network errors when internet is unreachable
          if (event.exception?.values?.some(isNetworkRequestFailedDueToUnreachableInternet)) {
            return null
          }

          // Apply production redacting if in production.
          if (isProduction) {
            event.exception?.values?.forEach((exception) => {
              exception.value = redactMessage(exception.value)
            })
            if (event.extra) {
              const extras = event.extra
              Object.entries(event.extra).forEach(
                ([key, value]) => (extras[key] = redactMessage(value)),
              )
            }
          }
          return event
        },
        replaysSessionSampleRate: isProduction ? 0.1 : 0.0,
        replaysOnErrorSampleRate: isProduction ? 1.0 : 0.0,
        integrations: [
          routingInstrumentationIntegration,
          Sentry.reactNativeTracingIntegration(),
          Sentry.reactNativeInfoIntegration(),
          Sentry.expoContextIntegration(),
          Sentry.httpContextIntegration(),
          Sentry.mobileReplayIntegration({
            maskAllText: false,
            maskAllImages: false,
            maskAllVectors: false,
          }),
        ],
      }),
})

if (Updates.updateId) {
  Sentry.setTags({
    ota: `${version}@${Updates.updateId}`,
  })
}

export function MesaSentry(props: Pick<PropsWithChildren, 'children'>) {
  const ref = useNavigationContainerRef()

  useEffect(() => {
    if (ref.current) {
      routingInstrumentationIntegration.registerNavigationContainer(ref)
    }
  }, [ref])

  return props.children
}

// this eslint-disable-next-line is being used on legacy code that i don't want to touch right now.
// it should not be a reason for you to `useUserStore` elsewhere in the codebase
// eslint-disable-next-line @typescript-eslint/no-deprecated
useUserStore.subscribe(
  (state) => state.userInformation,
  function updateSentryUser(userInformation) {
    const sentryUser: Sentry.User = {}
    if (userInformation?.id) sentryUser['id'] = userInformation.id
    if (userInformation?.email) sentryUser['email'] = userInformation.email
    Sentry.setUser(sentryUser)
  },
)
